import { Button } from "./button.js";
import { cn } from "./lib/cn.js";

import type { ReactNode } from "react";

/**
 * ErrorState primitive.
 *
 * Canon: docs/design-canon/01-ui/feedback-states.md R4 +
 *        docs/design-canon/04-shared/error-codes.md R7.
 *
 * Renders the user-facing surface for an error. Always shows the
 * `requestId` (per error-codes.md R7) so support can grep logs;
 * shows a Retry action when the caller provides one (recoverable
 * errors); falls back gracefully when the error has no code or
 * requestId (e.g. network refused).
 */
export interface ErrorStateProps {
  /** Headline. Defaults to a friendly generic. */
  title?: string;
  /** Detail line. Caller-provided message or canon-mapped fallback. */
  message?: string;
  /** Error code from `@l30/shared`'s `ERROR_CODES`. Surfaced as a
   * monospace tag for support. */
  code?: string;
  /** Request ID from the API error envelope. Surfaced for
   * support — required by canon for any error that has one. */
  requestId?: string;
  /** When provided, renders a Retry button calling this. */
  retry?: () => void;
  /** Optional secondary action (e.g. "Back to dashboard"). */
  secondaryAction?: ReactNode;
  className?: string;
}

export function ErrorState({
  title = "Something went wrong",
  message = "Please try again. If the problem persists, contact support and quote the request ID below.",
  code,
  requestId,
  retry,
  secondaryAction,
  className,
}: ErrorStateProps) {
  return (
    <div
      className={cn(
        "flex flex-col items-center justify-center gap-3 rounded-soft-lg border border-danger-border bg-danger-bg px-6 py-10 text-center",
        className,
      )}
      role="alert"
    >
      <h3 className="text-base font-semibold text-danger-fg">{title}</h3>
      <p className="max-w-md text-sm text-danger-fg">{message}</p>
      {(code || requestId) && (
        <div className="mt-1 space-y-0.5 font-mono text-xs text-danger-fg">
          {code && <div>code: {code}</div>}
          {requestId && <div>requestId: {requestId}</div>}
        </div>
      )}
      {(retry || secondaryAction) && (
        <div className="mt-2 flex flex-wrap items-center justify-center gap-2">
          {retry && (
            <Button type="button" onClick={retry}>
              Retry
            </Button>
          )}
          {secondaryAction}
        </div>
      )}
    </div>
  );
}
